import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import client from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

/** Compact search that impersonates a dealer on selection. */
export function ImpersonationSearch() {
  const { t } = useI18n();
  const { startImpersonation } = useAuth();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handler(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) {
      setItems([]);
      return;
    }
    let ignore = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const { data } = await client.get(`/dealers`, { params: { q, limit: 8 } });
        if (!ignore) setItems(data.items);
      } catch (e) {
        if (!ignore) setError(e?.response?.data?.detail || "Search failed");
      } finally {
        if (!ignore) setLoading(false);
      }
    }, 250);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [q]);

  const pick = async (d) => {
    setOpen(false);
    setQ("");
    await startImpersonation(d.id);
    navigate("/my-dealership");
  };

  return (
    <div className="relative w-72" ref={boxRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="impersonation-search"
          placeholder={t("impersonation.search")}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="h-9 pl-8 font-mono text-xs"
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      {open && (q?.length ?? 0) >= 2 && (
        <div
          data-testid="impersonation-results"
          className="absolute left-0 right-0 z-40 mt-1 max-h-80 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
        >
          {error && <div className="p-3 text-xs text-destructive">{String(error)}</div>}
          {!error && items.length === 0 && !loading && (
            <div className="p-3 text-xs text-muted-foreground">No dealers found.</div>
          )}
          {items.map((d) => (
            <button
              key={d.id}
              data-testid={`impersonation-pick-${d.id}`}
              onClick={() => pick(d)}
              className="flex w-full flex-col gap-0.5 border-b border-border px-3 py-2 text-left transition-colors hover:bg-muted"
            >
              <span className="text-sm font-medium leading-tight">{d.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {d.global_id}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
